import { insert, quickSort } from "../../utils/ArrayFunctions";
import WBSRowModel from "./WBSRowModel";

class WBSModel<T extends WBSRowModel> {
  private matrizRows: T[][] = [];
  private _count: number = 0;

  private _lastStructuredCode = "";
  private _fetchRowsIsComplete = false;

  constructor(
    public readonly uid: string,
    public readonly lengthLists: number = 1000
  ) {}

  get lastStructuredCode() {
    return this._lastStructuredCode;
  }

  get fetchRowsIsComplete() {
    return this._fetchRowsIsComplete;
  }

  get length(): number {
    return this._count;
  }

  countVisibleRows(showAt: "contract" | "measurement"): number {
    const rowsLists = this.getMatrizCopy();

    return rowsLists.reduce((count: number, list: T[]) => {
      const visiblesList = list.filter(
        (row) => row.visible && row.canShowAt(showAt)
      );
      count += visiblesList.length;
      return count;
    }, 0);
  }

  filter(callback: (row: T, index: number) => boolean) {
    let listFiltered = new Array<T>();
    const rowsLists = this.getMatrizCopy();

    for (const array of rowsLists) {
      const arrayFiltered = array.filter(callback);
      listFiltered = listFiltered.concat(arrayFiltered);
    }

    return listFiltered;
  }

  getAllRows(): T[] {
    const rowsLists = this.concatLists();
    return rowsLists;
  }

  getRowVisibleByIndex(
    index: number,
    showAt: "contract" | "measurement"
  ): T | undefined {
    let idx = index;
    const rowsLists = this.getMatrizCopy();

    for (const array of rowsLists) {
      const arrayFiltered = array.filter(
        (row) => row.visible && row.canShowAt(showAt)
      );

      if (idx <= arrayFiltered.length - 1) {
        return arrayFiltered[idx];
      }

      idx -= arrayFiltered.length;
    }

    return undefined;
  }

  getRowByUid(uid: string): T | undefined {
    const rowsLists = this.getMatrizCopy();
    for (const list of rowsLists) {
      const row = list.find((row) => row.uid === uid);
      if (row) return row;
    }
    return;
  }

  getRowByCode(code: string): T | undefined {
    const list = this.findRowsListByCode(code);
    return list?.find((row) => row.structured === code);
  }

  private findRowsListByCode(code: string): T[] | undefined {
    const codeStructured = WBSRowModel.getCodeStructured(code);
    return this.matrizRows.find((list) => {
      const lastChild = list[list.length - 1];
      return (
        this.compareRows({ codeStructured }, lastChild) ||
        this.equalRows({ codeStructured }, lastChild)
      );
    });
  }

  findVisibleRowIndexByCode(
    code: string,
    showAt: "contract" | "measurement"
  ): number {
    const rowsLists = this.getMatrizCopy();
    const codeStructured = WBSRowModel.getCodeStructured(code);
    let index = 0;

    for (const list of rowsLists) {
      const lastChild = list[list.length - 1];
      const listFiltered = list.filter(
        (row) => row.visible && row.canShowAt(showAt)
      );

      if (
        this.compareRows({ codeStructured }, lastChild) ||
        this.equalRows({ codeStructured }, lastChild)
      ) {
        const indexOnList = listFiltered.findIndex(
          (row) => row.structured === code
        );
        if (indexOnList >= 0) {
          index += indexOnList;
        }
      } else {
        index += listFiltered.length;
      }
    }

    return index;
  }

  getChildrenByUid(parentUid: string): T[] {
    let children = new Array<T>();
    const rowsLists = this.getMatrizCopy();

    for (const list of rowsLists) {
      let childrenAtList = list.filter(
        (row: T) => (row as any).parentStage === parentUid
      );
      children = children.concat(childrenAtList);
    }
    return children;
  }

  getChildrenByCode(parentCode: string): T[] {
    let children = new Array<T>();
    const rowsLists = this.getMatrizCopy();
    parentCode = parentCode + ".";

    for (const list of rowsLists) {
      let childrenAtList = list.filter((row: T) =>
        row.structured.startsWith(parentCode)
      );
      children = children.concat(childrenAtList);
    }
    return children;
  }

  getParentsByCode(code: string): T[] {
    let parents = new Array<T>();
    const rowsLists = this.getMatrizCopy();
    const parentsCode = this.getParentsCode(code);

    if (parentsCode.length)
      for (const list of rowsLists) {
        let parentsAtList = list.filter((row: T) =>
          parentsCode.includes(row.structured)
        );
        parents = parents.concat(parentsAtList);
      }

    return parents;
  }

  getStrutureByCode(code: string): T[] {
    let elements = new Array<T>();
    const rowsLists = this.getMatrizCopy();
    const parentCode = code + ".";
    const parentsCodes = this.getParentsCode(code);

    for (const list of rowsLists) {
      /** Obtem o elemento atual, suas etapas pais e seus filhos */
      let elementAtList = list.filter(
        (row: T) =>
          row.structured.startsWith(parentCode) ||
          row.structured === code ||
          parentsCodes.includes(row.structured)
      );
      elements = elements.concat(elementAtList);
    }
    return elements;
  }

  addRowList(newList: T[]) {
    this.setLastStructured(newList[newList.length - 1]?.structured);
    let list = [...newList];

    if (!this._fetchRowsIsComplete)
      console.log(
        "added",
        list?.length || 0,
        "rows to",
        this.uid,
        "last index",
        this.lastStructuredCode,
        "count:",
        this._count
      );

    if (this.matrizIsEmpty()) {
      quickSort(list, this.compareRows);
      this.splitList(list);
      return;
    }

    this.insertOrdered(list);
    this.splitListsBySize();
  }

  markAsComplete() {
    console.log("finished");
    this._fetchRowsIsComplete = true;
  }

  private setLastStructured(lastStructured: string = "") {
    this._lastStructuredCode = lastStructured;
  }

  private matrizIsEmpty() {
    return !this.matrizRows.length;
  }

  private insertOrdered(newList: T[]) {
    let lastRowListIndexFound = -1;
    let lastPositionFound = -1;

    for (let i = 0; i < newList.length; i++) {
      const position = this.insertRowOrdered(
        newList[i],
        lastRowListIndexFound,
        lastPositionFound
      );

      if (position) {
        lastRowListIndexFound = position.listIndex;
        lastPositionFound = position.rowIndex;
      }
    }
  }

  private insertRowOrdered(
    row: T,
    lastRowListIndexFound: number,
    lastPositionFound: number
  ): { listIndex: number; rowIndex: number } | undefined {
    const rowListIndex = this.findNextListIndex(row);
    if (rowListIndex < 0) {
      this.insertRowAtFinal(row);
      return;
    }

    const rowList = this.matrizRows[rowListIndex];
    if (this.compareRows(row, rowList[0])) {
      this.insertRowAtInicialOfList(row, rowList);
      return;
    }

    const position = this.findIndexToRow(
      row,
      rowList,
      lastPositionFound,
      lastRowListIndexFound === rowListIndex
    );

    this.insertRowAtPositionOfList(row, rowListIndex, position);

    return {
      listIndex: rowListIndex,
      rowIndex: position,
    };
  }

  private findNextListIndex(row: T) {
    return this.matrizRows.findIndex((list) => {
      return this.compareRows(row, list[list.length - 1]);
    });
  }

  private insertRowAtFinal(row: T) {
    const lastIndex = this.matrizRows.length - 1;
    const rowList = this.matrizRows[lastIndex];
    if (rowList) rowList.push(row);
  }

  private insertRowAtInicialOfList(row: T, list: T[]) {
    list.unshift(row);
  }

  private insertRowAtPositionOfList(
    row: T,
    listIndex: number,
    position: number
  ) {
    const rowList = this.matrizRows[listIndex];
    this.matrizRows[listIndex] = insert(rowList, position, row);
  }

  private findIndexToRow(
    row: T,
    list: T[],
    lastPositionFound: number,
    isLastListFound: boolean
  ) {
    /** Se maior que o ultimo elemento encontrado e menor que seu proximo,
     * insere após o elemento encontrado anteriormente e atualiza os dados.
     * OBS: Esta parte foi adicionada apenas para melhora na performance e só
     * funciona bem com este propósito se a lista estiver com a ordenação parcial no código estrutura. */
    if (isLastListFound && this.rowIsBetween(row, list, lastPositionFound)) {
      return lastPositionFound + 1;
    }

    return this.findIndexToRowOrdered(list, row);
  }

  private rowIsBetween(row: T, list: T[], position: number) {
    const rowBefore = list[position];
    const rowAfter = list[position + 1];
    return this.compareRows(rowBefore, row) && this.compareRows(row, rowAfter);
  }

  private findIndexToRowOrdered(list: T[], row: T): number {
    let init = 0;
    let end = list.length - 1;

    while (init < end) {
      let half = Math.ceil((init + end) / 2);
      const pivot = list[half];

      if (this.compareRows(row, pivot)) {
        //row menor que o pivot
        if (end === half) return half;
        end = half;
      } else if (this.equalRows(row, pivot)) {
        return half;
      } else {
        init = half;
      }
    }

    return init;
  }

  private splitListsBySize() {
    const newArray = this.concatLists();
    this.splitList(newArray);
  }

  private concatLists(): T[] {
    let newArray = new Array<T>();
    const rowsLists = this.getMatrizCopy();

    for (let i = 0; i < rowsLists.length; i++)
      newArray = newArray.concat(rowsLists[i]);

    return newArray;
  }

  private splitList(list: T[]) {
    let newMatriz: any = [];
    let i = 0;

    let initialPosition = 0;
    let finalPosition = this.lengthLists;

    this._count = list.length;

    while (initialPosition < list.length) {
      newMatriz[i] = list.slice(initialPosition, finalPosition);

      initialPosition = finalPosition;
      finalPosition += this.lengthLists;
      i++;
    }

    this.matrizRows = [...newMatriz];
  }

  private compareRows(x, y): boolean {
    return x.codeStructured < y.codeStructured;
  }

  private equalRows(x, y): boolean {
    return x.codeStructured === y.codeStructured;
  }

  private getParentsCode(code: string): string[] {
    let parents = new Array<string>();

    while (code !== "") {
      const lastPartIndex = code.lastIndexOf(".");
      code = code.substring(0, lastPartIndex);
      if (code) {
        parents.unshift(code);
      }
    }
    return parents;
  }

  private getMatrizCopy(): T[][] {
    return [...this.matrizRows];
  }
}

export default WBSModel;
